<template>
  <div class="d-flex flex-column">
    <div v-if="isLoading" class="d-flex justify-center align-center h-100">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="40"
        :width="2"
      />
    </div>
    <div v-else>
      <div class="ma-0 pa-0 d-flex flex-column mb-4">
        <span
          v-if="lastDepositDate"
          class="text-caption blue-grey--text"
          v-html="
            $t('ai_features.usage.last_deposit_date', {
              lastDepositDate: lastDepositDateText,
            })
          "
        />

        <span
          class="text-caption blue-grey--text"
          v-html="
            $t('ai_features.usage.last_update', {
              lastUpdate: lastUpdatedText,
            })
          "
        />
      </div>

      <v-row class="ma-0 pa-0">
        <v-col cols="auto" class="mr-4 ml-0 pl-0">
          <div>
            <div class="ma-0 pa-0 d-flex align-center">
              <span
                class="h3 pa-0 ma-0"
                :class="{
                  'blue-grey--text text--darken-2': remainingTokens > 0,
                  'red--text': remainingTokens <= 0,
                }"
                >{{ formattedRemainingTokens }}
              </span>

              <v-tooltip v-if="remainingTokens <= 0" bottom max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-on="on" v-bind="attrs" class="ms-2" color="red">
                    mdi-alert-circle-outline
                  </v-icon>
                </template>

                <span>{{
                  $t('ai_features.usage.exhausted_tokens_helper')
                }}</span>
              </v-tooltip>
            </div>

            <div class="mt-1">
              <span class="body-2 blue-grey--text text--darken-1">
                {{ $t('ai_features.usage.remaining_tokens') }}</span
              >
            </div>
          </div>
        </v-col>

        <v-divider vertical />

        <v-col class="d-flex align-end ml-4">
          <div>
            <div class="ma-0 pa-0 align-end">
              <span
                class="h3 pa-0 ma-0 blue-grey--text text--darken-2 font-weight-regular"
                >{{ formattedAverageMonthlyConsumption }}
              </span>
            </div>

            <div class="mt-1">
              <span class="body-2 blue-grey--text text--darken-1">
                {{ $t('ai_features.usage.monthly_consumption_average') }}</span
              >
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import { utils } from 'frontend-js-shared'

const DEFAULT_LANGUAGE = 'en-US'

export default {
  name: 'TokenUsagePanel',

  mixins: [mixin],

  props: {
    organizationId: {
      type: String,
      required: false,
    },
  },

  mounted() {
    this.fetchTokenUsageData()
  },

  data() {
    return {
      averageMonthlyConsumption: 0,
      remainingTokens: 0,
      lastDepositDate: null,
      isLoading: false,
    }
  },

  computed: {
    ...mapGetters({
      account: 'account',
      organization: 'organization',
      currentAccountOrganizationId: 'organizationId',
      language: 'language',
    }),

    organizationName() {
      return this.organization?.name || ''
    },

    formattedAverageMonthlyConsumption() {
      return this.formatNumberToText(this.averageMonthlyConsumption)
    },
    formattedRemainingTokens() {
      return this.formatNumberToText(this.remainingTokens)
    },

    lastDepositDateText() {
      return utils.dates.formatDate(this.lastDepositDate, 'MMM D YYYY h:mm A')
    },

    lastUpdatedText() {
      return utils.dates.formatDate(new Date(), 'MMM D YYYY h:mm A')
    },

    userLanguage() {
      return window.navigator.language || DEFAULT_LANGUAGE
    },
  },

  methods: {
    async fetchTokenUsageData() {
      this.isLoading = true

      try {
        const { data } = await api.ai.getBalance(
          this.organizationId ?? this.currentAccountOrganizationId
        )
        const {
          averageMonthlyConsumption,
          remainingTokens,
          lastDepositDate,
        } = data

        this.remainingTokens = remainingTokens ?? 0
        this.averageMonthlyConsumption = averageMonthlyConsumption ?? 0
        this.lastDepositDate = lastDepositDate ?? null

        this.isLoading = false
      } catch (error) {
        this.showError(this.$t('ai_features.usage.fetch_error'))
        this.isLoading = false
      }
    },

    formatNumberToText(value) {
      return Intl.NumberFormat(this.userLanguage, {
        maximumFractionDigits: 0,
      }).format(value)
    },
  },
}
</script>
